import React, { useEffect, useMemo, useState } from 'react'
import Dropdown from 'core/elements/dropdown'
import withProgress from 'core/components/progress/withProgress'
import useListAction from 'core/hooks/useListAction'
import { listTenantDefaultVolumeType, listVolumeTypes } from './actions'
import { useSelector } from 'react-redux'
import { defaultTenantVolumeTypesSelector, volumeTypesSelector } from './selectors'
import { getValidVolumeTypes } from '../volumes/helpers'
import { resmgrHostsSelector } from '../../infrastructure/selectors'
import { listResmgrHosts } from '../../infrastructure/actions'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

export default function VolumeTypesPicklist({
  disabled = false,
  name = 'volumeTypes',
  label = 'Volume Type',
  includeNoneOption = false,
  filterFn = null,
  value = '',
  loading = false,
  onChange,
  overrideDefaultVolumeType = null,
  ...rest
}) {
  const [activeVolumeBackends, setActiveVolumeBackends] = useState([])
  const [loadingVolumeBackends, setLoadingVolumeBackends] = useState(true)

  const { loading: loadingVolumeTypes } = useListAction(listVolumeTypes)
  const volumeTypes = useSelector(volumeTypesSelector)
  const { loading: loadingDefaultVolumeType } = useListAction(listTenantDefaultVolumeType)
  const defaultVolumeType = useSelector(defaultTenantVolumeTypesSelector)

  const { loading: loadingHost } = useListAction(listResmgrHosts)
  const hosts = useSelector(resmgrHostsSelector)

  useEffect(() => {
    if (hosts.length === 0) {
      return
    }

    getValidVolumeTypes({ hosts, setActiveVolumeBackends, setLoadingVolumeBackends })
  }, [hosts])

  // For other tenants we need to fetch their default volume type and show it selected
  // So we override the defaultVolumeType with overrideDefaultVolumeType
  // Other tenants will not have overrideDefaultVolumeType
  const defaultVolumeTypeToShow = overrideDefaultVolumeType
    ? overrideDefaultVolumeType
    : defaultVolumeType

  const options = useMemo(() => {
    if (loadingDefaultVolumeType || loadingVolumeTypes) {
      return []
    }

    const volumeTypeOptions = volumeTypes.map((volumeType) => ({
      label:
        defaultVolumeTypeToShow.volume_type_id === volumeType.id
          ? `${volumeType.name || volumeType.id} (Default)`
          : volumeType.name || volumeType.id,
      value: volumeType.name || volumeType.id,
    }))

    const filterTypeOptions = volumeTypeOptions.filter((option) => {
      const backendName = volumeTypes.find((volume) => volume.name === option.value)?.extra_specs
        ?.volume_backend_name

      return activeVolumeBackends.includes(backendName)
    })

    const filteredOptions = filterFn ? filterFn(filterTypeOptions) : filterTypeOptions
    return includeNoneOption ? [{ label: 'None', value: '' }, ...filteredOptions] : filteredOptions
  }, [
    volumeTypes,
    includeNoneOption,
    filterFn,
    defaultVolumeTypeToShow,
    activeVolumeBackends,
    loadingVolumeTypes,
    loadingDefaultVolumeType,
  ])

  useEffect(() => {
    if (loadingDefaultVolumeType || loadingVolumeTypes) {
      return
    }

    if (defaultVolumeTypeToShow?.volume_type_id && volumeTypes.length > 0) {
      const filteredType = volumeTypes.find(
        (type) => type.id === defaultVolumeTypeToShow.volume_type_id,
      )
      onChange((filteredType?.name || filteredType?.id) ?? '')
    } else {
      onChange(value ? value : options[0]?.value)
    }
  }, [defaultVolumeTypeToShow, volumeTypes, options])

  const loadingSomething =
    loadingVolumeTypes || loadingDefaultVolumeType || loadingHost || loadingVolumeBackends

  return (
    <AsyncDropdown
      {...rest}
      value={value}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loadingSomething}
      items={options}
      onChange={onChange}
    />
  )
}
